import React, {Fragment, useEffect, useRef, useState} from 'react';

import {
    Col,
    Container,
    Row,
    CardHeader
} from "reactstrap";
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import PageTitleAlt2 from '../../Layout/AppMain/PageTitleAlt2';

import {getDashboard, downloadDashboardCsv} from '../../_providers/dashboardProvider';

import {translate} from 'react-admin';

const REFRESH_INTERVAL = 5 * 60 * 1000;
const DAY_OPTIONS = [1, 7, 30, 90];

const computeDateRange = (days) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
};

const Dashboard = props => {
    const [dashboard, setDashboard] = useState({
        customers: {},
        androidSubs: [],
        iphoneSubs: [],
        totalCompletedResellers: [],
        totalCompletedWebsite: []
    });
    const [newCustomerDays, setNewCustomerDays] = useState(1);
    const [newCustomerLoading, setNewCustomerLoading] = useState(true);
    const newCustomerDaysRef = useRef(newCustomerDays);

    const fetchDashboard = (days, showLoader = false) => {
        if (showLoader) setNewCustomerLoading(true);
        getDashboard(days).then(data => {
            setDashboard(data);
            setNewCustomerLoading(false);
        });
    };

    // Keep ref in sync so the interval always uses the latest value
    useEffect(() => {
        newCustomerDaysRef.current = newCustomerDays;
    }, [newCustomerDays]);

    // Fetch when days filter changes — show loader
    useEffect(() => {
        fetchDashboard(newCustomerDays, true);
    }, [newCustomerDays]);

    // Silent auto-refresh every 5 minutes — set up once, cleaned up on unmount
    useEffect(() => {
        const interval = setInterval(() => {
            fetchDashboard(newCustomerDaysRef.current);
        }, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, []);

    const handleDownload = (endpoint, filename) => {
        const { from, to } = computeDateRange(newCustomerDays);
        downloadDashboardCsv(endpoint, filename, from, to);
    };

    return (
        <Fragment>
            <ReactCSSTransitionGroup
                component="div"
                transitionName="TabsAnimation"
                transitionAppear={true}
                transitionAppearTimeout={0}
                transitionEnter={false}
                transitionLeave={false}>
                <PageTitleAlt2
                    heading={props.translate("resources.dashboard.name")}
                    icon="pe-7s-graph icon-gradient bg-ripe-malin"
                />
                <Fragment>
                    <Container fluid>
                        <Row>
                            <Col md="6" xl="3">
                                <div className="card mb-3 widget-content">
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.active_customers_purchases_count")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-success">
                                                    {dashboard.customers.activeCustomersPurchasesCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6" xl="3">
                                <div className="card mb-3 widget-content">
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.active_customers_subs_count")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-success">
                                                    {dashboard.customers.activeCustomersSubsCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6" xl="3">
                                <div className="card mb-3 widget-content">
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.active_customers_test_count")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-success">
                                                    {dashboard.customers.activeCustomersTestCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6" xl="3">
                                <div className="card mb-3 widget-content">
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.expired_new_customers_30")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-success">
                                                    {dashboard.customers.expiredNewCustomers30}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <CardHeader className="mbg-3 h-auto pl-0 pr-0 bg-transparent no-border">
                            <div className="d-flex align-items-center">
                                <div className="card-header-title fsize-2 text-capitalize font-weight-normal mr-3">
                                    {props.translate("resources.dashboard.new_customer_days_label")}
                                </div>
                                <select
                                    value={newCustomerDays}
                                    onChange={e => setNewCustomerDays(Number(e.target.value))}
                                    className="form-control"
                                    style={{width: 'auto'}}
                                >
                                    {DAY_OPTIONS.map(d => (
                                        <option key={d} value={d}>
                                            {props.translate("resources.dashboard.last_n_days", { n: d })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </CardHeader>

                        <Row>
                            <Col md="6" xl="4">
                                <div
                                    className="card mb-3 widget-content"
                                    onClick={() => handleDownload('new-customers/export', 'new_customers.csv')}
                                    style={{cursor: 'pointer'}}
                                    title={props.translate("resources.dashboard.download_csv")}
                                >
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.new_customers")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-info">
                                                    {newCustomerLoading
                                                        ? <span className="spinner-border spinner-border-sm" />
                                                        : dashboard.customers.newCustomersCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6" xl="4">
                                <div
                                    className="card mb-3 widget-content"
                                    onClick={() => handleDownload('new-paying-customers/export', 'new_paying_customers.csv')}
                                    style={{cursor: 'pointer'}}
                                    title={props.translate("resources.dashboard.download_csv")}
                                >
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.new_paying_customers")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-info">
                                                    {newCustomerLoading
                                                        ? <span className="spinner-border spinner-border-sm" />
                                                        : dashboard.customers.newPayingCustomersCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="6" xl="4">
                                <div
                                    className="card mb-3 widget-content"
                                    onClick={() => handleDownload('new-paying-no-activity/export', 'new_paying_no_activity.csv')}
                                    style={{cursor: 'pointer'}}
                                    title={props.translate("resources.dashboard.download_csv")}
                                >
                                    <div className="widget-content-outer">
                                        <div className="widget-content-wrapper">
                                            <div className="widget-content-left">
                                                <div className="widget-heading">
                                                    {props.translate("resources.dashboard.new_paying_no_activity")}
                                                </div>
                                            </div>
                                            <div className="widget-content-right">
                                                <div className="widget-numbers text-info">
                                                    {newCustomerLoading
                                                        ? <span className="spinner-border spinner-border-sm" />
                                                        : dashboard.customers.newPayingNoActivityCount}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <CardHeader className="mbg-3 h-auto pl-0 pr-0 bg-transparent no-border">
                            <div className="card-header-title fsize-2 text-capitalize font-weight-normal">
                                {props.translate("resources.dashboard.iphone_title")}
                            </div>
                        </CardHeader>

                        {
                            dashboard.iphoneSubs.map((is) => {
                                return (<Row key={is.p_id}>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate(`resources.dashboard.count`)}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.count}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate("resources.dashboard.sum")}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.sum} ₪
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>)
                            })

                        }

                        <CardHeader className="mbg-3 h-auto pl-0 pr-0 bg-transparent no-border">
                            <div className="card-header-title fsize-2 text-capitalize font-weight-normal">
                                {props.translate("resources.dashboard.android_title")}
                            </div>
                        </CardHeader>

                        {
                            dashboard.androidSubs.map((is) => {
                                return (<Row key={is.p_id}>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate(`resources.dashboard.count`)}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.count}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate("resources.dashboard.sum")}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.sum} ₪
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>)
                            })

                        }

                        <CardHeader className="mbg-3 h-auto pl-0 pr-0 bg-transparent no-border">
                            <div className="card-header-title fsize-2 text-capitalize font-weight-normal">
                                {props.translate("resources.dashboard.reseller_title")}
                            </div>
                        </CardHeader>

                        {
                            dashboard.totalCompletedResellers.map((is) => {
                                return (<Row key={is.p_id}>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate(`resources.dashboard.count`)}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.count}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate("resources.dashboard.sum")}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.sum} ₪
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>)
                            })

                        }

                        <CardHeader className="mbg-3 h-auto pl-0 pr-0 bg-transparent no-border">
                            <div className="card-header-title fsize-2 text-capitalize font-weight-normal">
                                {props.translate("resources.dashboard.website_title")}
                            </div>
                        </CardHeader>

                        {
                            dashboard.totalCompletedWebsite.map((is) => {
                                return (<Row key={is.p_id}>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate(`resources.dashboard.count`)}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.count}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6" xl="6">
                                        <div className="card mb-3 widget-content">
                                            <div className="widget-content-outer">
                                                <div className="widget-content-wrapper">
                                                    <div className="widget-content-left">
                                                        <div className="widget-heading">
                                                            {props.translate("resources.dashboard.sum")}: {is.product}
                                                        </div>
                                                    </div>
                                                    <div className="widget-content-right">
                                                        <div className="widget-numbers text-success">
                                                            {is.sum} ₪
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>)
                            })

                        }

                    </Container>
                </Fragment>
            </ReactCSSTransitionGroup>
        </Fragment>
    )

};

export default translate(Dashboard);